import { FC, useMemo } from 'react'

import { TypeStyle } from '@syconium/little-miss-figgy'

import { usePortal } from '../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../containers/EmbroideryService'

import { EmbroideryCta } from './EmbroideryCta'
import { PersonalizeSection } from './PersonalizeSection'
import { TextEmbroiderySection } from './TextEmbroiderySection'

interface Props {
  isPortalColorSelected?: boolean
  isPortalSelectionInvalid?: boolean
}

export const EmbroideryCtaSection: FC<Props> = ({
  isPortalColorSelected,
  isPortalSelectionInvalid,
}) => {
  const { portal, hasMandatoryTextEmbroidery, hasMandatoryLogoEmbroidery } = usePortal()
  const { productCategory } = EmbroideryService.useContainer()

  const { embroidery, name } = portal ?? {}
  const { embroideryDiscountPercentage } = embroidery ?? {}
  const { embroidery: embroideryStrings } = useTranslation()

  // Check if current portal enforces embroidery constraints for the product category
  const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery(productCategory)
  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery(productCategory)

  // Create an array of copy lines to display for the "personalize" section
  const personalizeCopyLines = useMemo(() => {
    // If the current color isn't a portal color & no mandatory restrictions are defined for embroidery,
    // don't return any copy for the personalize section
    if (!isPortalColorSelected || (!mandatoryLogoEmbroidery && !mandatoryTextEmbroidery)) return []

    // Compute the first copy line
    const topLine = (() => {
      let copy = null
      if (mandatoryTextEmbroidery && mandatoryLogoEmbroidery)
        copy = embroideryStrings.personalizeSection.logoAndTextEmbroideryRequired
      else if (mandatoryLogoEmbroidery)
        copy = embroideryStrings.personalizeSection.logoEmbroideryRequired
      else if (mandatoryTextEmbroidery)
        copy = embroideryStrings.personalizeSection.textEmbroideryRequired

      if (!copy) return null
      return `${name} ${copy}.`
    })()

    // Compute the discount copy line
    const discountLine = embroideryDiscountPercentage
      ? embroideryStrings.personalizeSection.discountText.replace(
          '{{discountPercentage}}',
          embroideryDiscountPercentage.toString()
        ) + '.'
      : null

    const placementNoticeLine = isPortalColorSelected
      ? embroideryStrings.personalizeSection.embroideryPlacementNotice
      : null

    return [topLine, discountLine, placementNoticeLine].filter(Boolean)
  }, [
    embroideryDiscountPercentage,
    embroideryStrings,
    isPortalColorSelected,
    mandatoryTextEmbroidery,
    mandatoryLogoEmbroidery,
    name,
  ])

  const displayPortalEmbroideryFeatures =
    isPortalColorSelected && (mandatoryLogoEmbroidery || mandatoryTextEmbroidery)

  return (
    <>
      <PersonalizeSection.Wrapper
        hasBorder={displayPortalEmbroideryFeatures}
        isPortalColorSelected={isPortalColorSelected}
      >
        {displayPortalEmbroideryFeatures ? (
          <PersonalizeSection.Text>
            {personalizeCopyLines.map(copy => (
              <TypeStyle.BodySmall asTag='p' fontWeight={500} key={copy}>
                {copy}
              </TypeStyle.BodySmall>
            ))}
          </PersonalizeSection.Text>
        ) : (
          <EmbroideryCta showLowestPrice />
        )}
      </PersonalizeSection.Wrapper>
      {displayPortalEmbroideryFeatures && (
        <TextEmbroiderySection
          isInvalid={isPortalSelectionInvalid}
          isRequired={mandatoryTextEmbroidery}
        />
      )}
    </>
  )
}
